import { addDoc, collection, doc, getDoc, getDocs } from 'firebase/firestore';

import {
  IAudioCardItem,
  ICard,
  ICheckListCardItem,
  IImageCardItem,
  IMultipleChoiceCardItem,
  IOpenQuestionCardItem,
  IPollCardItem,
  IVideoCardItem,
  TCardItem,
} from 'types/card';
import { IMuxVideo } from 'types/story';

import {
  duplicateMedia,
  duplicateVideo,
} from 'services/api/firebase/callables';
import {
  isAudioCardItem,
  isCheckListCardItem,
  isImageCardItem,
  isMultipleChoiceCardItem,
  isOpenQuestionCardItem,
  isPollCardItem,
  isVideoCardItem,
} from 'services/types';
import { getShortUniqueId } from 'services/uniqueId';

import { auth, db } from 'clients/firebase';

export const duplicateCard = async ({
  card,
  storyId,
}: {
  card: ICard;
  storyId: string;
}): Promise<ICard> => {
  return {
    ...card,
    id: getShortUniqueId(),
    background: card.background,
    meta: { ...card.meta, order: card.meta.order + 1 },
    items: await duplicateCardItems({
      items: card.items,
      storyId,
      newStoryId: storyId,
    }),
  };
};

export const duplicateCardItems = async ({
  items,
  storyId,
  newStoryId,
}: {
  items: TCardItem[];
  storyId: string;
  newStoryId: string;
}) => {
  return (
    await Promise.all(
      items.map(async item => {
        if (isAudioCardItem(item)) {
          return duplicateMediaCardItem(item, storyId, newStoryId);
        }

        if (isImageCardItem(item)) {
          return duplicateMediaCardItem(item, storyId, newStoryId);
        }

        if (isVideoCardItem(item)) {
          return duplicateVideoCardItem(item, newStoryId);
        }

        if (isMultipleChoiceCardItem(item)) {
          return duplicateMultipleChoiceCardItem(item);
        }

        if (isCheckListCardItem(item)) {
          return duplicateChecklistCardItem(item);
        }

        if (isPollCardItem(item)) {
          return duplicatePollCardItem(item);
        }

        if (isOpenQuestionCardItem(item)) {
          return duplicateOpenQuestionItem(item);
        }

        return item;
      }),
    )
  ).filter(i => i) as TCardItem[];
};

export const duplicateMediaCardItem = async <
  ItemType extends IAudioCardItem | IImageCardItem,
>(
  item: ItemType,
  storyId: string,
  duplicatedStoryId: string,
) => {
  const newUniqueId = getShortUniqueId();

  if (isImageCardItem(item)) {
    const index = item.body.path.search(storyId);
    if (index === -1) {
      // Unsplash images
      return item;
    }
  }

  const newPath =
    item.body.path.replace(storyId, duplicatedStoryId) + newUniqueId;

  const {
    data: { fileUrl },
  } = await duplicateMedia({
    itemPath: item.body.path,
    newItemPath: newPath,
  });

  let resizedImageBody: { linkResized?: string; pathResized?: string } = {};

  if (isImageCardItem(item) && item.body.pathResized) {
    const newPathResized =
      item.body.path.replace(storyId, duplicatedStoryId) +
      newUniqueId +
      '_resized';

    const {
      data: { fileUrl },
    } = await duplicateMedia({
      itemPath: item.body.pathResized,
      newItemPath: newPathResized,
    });

    resizedImageBody.linkResized = fileUrl;
    resizedImageBody.pathResized = newPathResized;
  }

  return {
    ...item,
    body: {
      ...item.body,
      link: fileUrl,
      path: newPath,
      ...resizedImageBody,
    },
  };
};

export const duplicateVideoCardItem = async (
  item: IVideoCardItem,
  duplicatedStoryId: string,
) => {
  const videoDoc = await await getDoc(
    doc(db, `Videos/${item.body.fireStoreId}`),
  );

  const video = videoDoc.data() as IMuxVideo;

  if (video?.mux?.playback_ids?.[0].id) {
    const duplicatedVideoRef = await addDoc(collection(db, 'Videos'), {
      storyId: duplicatedStoryId,
      fileName: video.fileName,
    });

    await duplicateVideo({
      fireStoreId: duplicatedVideoRef.id,
      muxPlaybackId: video.mux.playback_ids[0].id,
      muxAssetId: video.mux.id,
    });

    return {
      ...item,
      body: { ...item.body, fireStoreId: duplicatedVideoRef.id },
    };
  }
  if (!videoDoc.exists()) {
    return {
      ...item,
      body: { ...item.body, fireStoreId: item.body.fireStoreId },
    };
  }
};

export const duplicateMultipleChoiceCardItem = async (
  item: IMultipleChoiceCardItem,
) => {
  const question = (
    await getDoc(doc(db, `MultipleChoiceQuestions/${item.body.questionId}`))
  ).data();

  const questionsRef = collection(db, 'MultipleChoiceQuestions');
  const questionRef = await addDoc(questionsRef, {
    ...question,
    userId: auth.currentUser?.uid,
  });

  const optionsRef = collection(
    db,
    `MultipleChoiceQuestions/${questionRef.id}/Options`,
  );
  const optionDocs = (
    await getDocs(
      collection(db, `MultipleChoiceQuestions/${item.body.questionId}/Options`),
    )
  ).docs;
  optionDocs.forEach(optionDoc => {
    addDoc(optionsRef, { ...optionDoc.data(), count: 0 });
  });

  return {
    ...item,
    body: { questionId: questionRef.id },
  };
};

export const duplicateChecklistCardItem = async (item: ICheckListCardItem) => {
  const checklist = (
    await getDoc(doc(db, `Checklists/${item.body.checklistId}`))
  ).data();

  const checklistsRef = collection(db, 'Checklists');
  const checklistRef = await addDoc(checklistsRef, {
    ...checklist,
    userId: auth.currentUser?.uid,
  });

  const itemsRef = collection(db, `Checklists/${checklistRef.id}/Items`);
  const itemsDocs = (
    await getDocs(collection(db, `Checklists/${item.body.checklistId}/Items`))
  ).docs;
  itemsDocs.forEach(itemDoc => {
    addDoc(itemsRef, { ...itemDoc.data() });
  });

  return {
    ...item,
    body: { checklistId: checklistRef.id },
  };
};

export const duplicatePollCardItem = async (item: IPollCardItem) => {
  const poll = (await getDoc(doc(db, `Polls/${item.body.pollId}`))).data();

  const pollsRef = collection(db, 'Polls');
  const pollRef = await addDoc(pollsRef, {
    ...poll,
    userId: auth.currentUser?.uid,
  });

  const optionsRef = collection(db, `Polls/${pollRef.id}/Options`);
  const optionDocs = (
    await getDocs(collection(db, `Polls/${item.body.pollId}/Options`))
  ).docs;
  optionDocs.forEach(optionDoc => {
    addDoc(optionsRef, { ...optionDoc.data(), count: 0 });
  });

  return { ...item, body: { pollId: pollRef.id } };
};

export const duplicateOpenQuestionItem = async (
  item: IOpenQuestionCardItem,
) => {
  const question = (
    await getDoc(doc(db, `OpenQuestions/${item.body.questionId}`))
  ).data();
  const questionsRef = collection(db, 'OpenQuestions');
  const questionRef = await addDoc(questionsRef, {
    ...question,
    userId: auth.currentUser?.uid,
  });

  return {
    ...item,
    body: { questionId: questionRef.id },
  };
};
